import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import isArchived from 'archive/isArchived';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FieldTable, {ClientSchemaField} from 'registry/field/FieldTable';
import StorableFieldTableRow from 'registry/field/StorableFieldTableRow';
import {UpdateContainerSchemaParams} from 'samples/SampleSchemaActions';
import {ContainerSchema, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import SchemaDescriptionInput from 'schemas/SchemaDescriptionInput';
import SchemaWarehouseNameField from 'schemas/SchemaWarehouseNameField';
import {StorableSchemaSettingProps} from 'settings/samples/StorableSchemaSettingsConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import EditableTextField from 'ui-library/inputs/EditableTextField';

import ContainerSchemaSubheader from './ContainerSchemaSubheader';

export interface ContainerSchemaSettingsProps extends StorableSchemaSettingProps {
  containerSchema: ContainerSchema;
  onArchiveContainerSchema: (containerSchemaId: string) => void;
  onConfirmDeleteField: (onSubmit: (param?: string) => void) => void;
  onUnarchiveContainerSchema: (containerSchemaId: string) => void;
  onUpdateContainerSchema: (containerSchemaId: string, updateParams: UpdateContainerSchemaParams) => void;
  ownerId: string;
  storableSchemas: Array<StorableSchema>;
  dataTestAttrs?: DataTestAttrs;
}

export default function ContainerSchemaSettings(props: ContainerSchemaSettingsProps): JSX.Element {
  const isWritable = (): boolean => props.isWritable && !isArchived(props.containerSchema);

  const shouldSubmitNewFields = (newFields: Array<ClientSchemaField>): boolean =>
    // eslint-disable-next-line no-restricted-syntax
    !_.some(newFields, (field) => field.name.length === 0);

  const handleUpdateContainerSchema = (
    editedContainerSchemaProps: Partial<UpdateContainerSchemaParams>
  ): void => {
    // Endpoint expects all fields, so we need to merge edited fields with the original ones
    props.onUpdateContainerSchema(props.containerSchema.id, {
      ...props.containerSchema,
      ...editedContainerSchemaProps,
    });
  };

  const {containerSchema, savingState, onConfirmDeleteField} = props;
  return (
    <div
      className={classNames('sampleSchemaSettings-containerTable', {
        'u-faded': isArchived(containerSchema),
      })}
      key={containerSchema.id}
      {...getTestProps({component: 'ContainerSchemaSettings', element: 'root', key: containerSchema.id})}
      data-amp-safe={false}
    >
      <div className="sampleSchemaSettings-containerTable-name">
        <div className="sampleSchemaSettings-containerTable-nameContainer">
          <div className="sampleSchemaSettings-containerTable-nameHeader">Name</div>
          {isWritable() ? (
            <EditableTextField
              requestChange={(newName: string) => {
                handleUpdateContainerSchema({name: newName});
              }}
              isDisabled={false}
              value={containerSchema.name}
              dataTestAttrs={{
                component: 'ContainerSchemaSettings',
                element: 'nameField',
              }}
            />
          ) : (
            <span>
              {containerSchema.name}
              <ArchiveRecordLabel item={containerSchema} />
            </span>
          )}
        </div>
        <div className="sampleSchemaSettings-containerTable-nameContainer">
          <div className="sampleSchemaSettings-containerTable-nameHeader">ID Prefix</div>
          <EditableTextField
            requestChange={(newPrefix: string) => {
              handleUpdateContainerSchema({prefix: newPrefix});
            }}
            isDisabled={!isWritable()}
            value={containerSchema.prefix}
            dataTestAttrs={{
              component: 'ContainerSchemaSettings',
              element: 'prefixField',
            }}
          />
        </div>
        <div className="sampleSchemaSettings-containerTable-nameContainer">
          <SchemaWarehouseNameField
            onChange={(newSqlIdentifier) => {
              handleUpdateContainerSchema({sqlIdentifier: newSqlIdentifier});
            }}
            forContainerSchema={true}
            isWritable={isWritable()}
            sqlIdentifier={containerSchema.sqlIdentifier}
          />
        </div>
      </div>
      <SchemaDescriptionInput
        value={containerSchema.schemaDescription ?? null}
        onChange={(description: string) => {
          handleUpdateContainerSchema({schemaDescription: description});
        }}
        isWritable={isWritable()}
        isBeingCreated={props.isBeingCreated}
      />
      <FieldTable // TODO (BNCH-53233): Render FieldTableLoading while fetching data
        storableSchemas={props.storableSchemas}
        schemaId={containerSchema.id}
        schema={containerSchema}
        fields={containerSchema.allFields}
        isBeingCreated={props.isBeingCreated}
        shouldShowIsParentLink={false}
        shouldShowIsRequired={true}
        shouldShowIsIntegration={false}
        shouldShowTooltipText={true}
        isDisabled={!isWritable()}
        handleDeleteConfirmation={onConfirmDeleteField}
        itemClass={StorableFieldTableRow}
        onFieldsChange={(fields) => {
          if (shouldSubmitNewFields(fields)) {
            handleUpdateContainerSchema({allFields: fields});
          }
        }}
        registryId={props.registryId}
        ownerUrlId={props.ownerUrlId}
        ownerId={props.ownerId}
        savingState={savingState}
        schemaType={ModelTypeConstants.CONTAINER}
        subheader={
          <ContainerSchemaSubheader
            containerSchema={containerSchema}
            isWritable={props.isWritable}
            onArchiveContainerSchema={props.onArchiveContainerSchema}
            onUnarchiveContainerSchema={props.onUnarchiveContainerSchema}
          />
        }
        dataTestAttrs={props.dataTestAttrs}
      />
    </div>
  );
}
